/**
 * Retry transient network / gateway failures once (same idea as discovery-app).
 */

const RETRY_DELAY_MS = 500;
const MAX_RETRIES = 1;

function isTransientFailure(error: unknown, status?: number): boolean {
  if (status !== undefined) {
    if (status >= 400 && status < 500 && status !== 429) return false;
    if (status >= 502 && status <= 503) return true;
    if (status >= 500) return true;
  }
  const message = error instanceof Error ? error.message : String(error);
  if (/network|failed to fetch|load failed|timeout|aborted/i.test(message)) return true;
  return false;
}

function getStatusFromError(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error && typeof (error as { status: number }).status === 'number') {
    return (error as { status: number }).status;
  }
  return undefined;
}

export async function withRetry<T>(fn: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      const status = getStatusFromError(error);
      if (attempt < MAX_RETRIES && isTransientFailure(error, status)) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        continue;
      }
      throw error;
    }
  }
  throw lastError;
}
